import { Component, inject, Input, OnInit } from '@angular/core';
import { StateBodyCorporate } from '../../../models/body-corporate.model';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { Roles, ROLES_MESSAGE_MAP} from "../../../../core/constants/roles.enum";
import { Attachment, DocumentType } from '../../../models/attachment.model';
import { AttachmentPreviewManager } from '../../../managers/attachment-preview.manager';
import { AttachmentService } from '../../../services/attachment.service';
import { DocumentPreviewManager } from '../../../../features/application/managers/document-preview.manager';
import { RegEntityDocumentPreviewManager } from '../../../managers/document-preview.manager';

@Component({
    selector: 'app-body-corporate-modal',
    templateUrl: './body-corporate-modal.component.html',
    styleUrls: ['./body-corporate-modal.component.scss'],
    standalone: false
})
export class BodyCorporateModalComponent implements OnInit {

  isVisible: boolean = true;

  //@ts-ignore
  @Input() bodycorp: StateBodyCorporate;
  @Input() isPayer: boolean = false;
  @Input() isRecipient: boolean = false;
  @Input() isEntity: boolean = false;
  excludedRoles: Roles[] = [Roles.PAYR, Roles.PCNT, Roles.RCPT];

  qualifiedRecipientLines: string[] = [];
  certificates: Attachment[] = [];
  worldCheckDocuments: Attachment[] = [];
  kybOrganizationCheck: Attachment | null = null;

  constructor(public modal: NzModalRef<StateBodyCorporate>, 
    private attachmentService: AttachmentService,
    private attachmentPreviewManager: AttachmentPreviewManager,
    private documentPreviewManager: DocumentPreviewManager,
    private regEntityDocumentPreviewManager: RegEntityDocumentPreviewManager) {

  }

  ngOnInit(): void {
    this.bodycorp = this.nzModalData.bodycorp;
    this.isPayer = this.nzModalData.isPayer;
    this.isRecipient = this.nzModalData.isRecipient;
    this.isEntity = this.nzModalData.isEntity;

    this.setQualifiedRecipientLines();
    this.certificates = this.getCertificates(this.bodycorp.incorporationCertificateDocIds);
    this.worldCheckDocuments = this.getWorldCheckDocuments(this.bodycorp.worldCheckDocIds);
    this.kybOrganizationCheck = this.bodycorp.kybDocumentId
      ? { documentId: this.bodycorp.kybDocumentId, fileName: 'KYB Organization Check.pdf' } as Attachment
      : null;
  }

  private getCertificates(ids: number[]): Attachment[] {
    return (ids || [])
      .map(id => this.getCertificate(id))
      .filter((att): att is Attachment => att != null);
  }

  private getCertificate(id: number): Attachment | null {
    let result: Attachment | null = null;

    // Registered Entity
    if (this.isEntity && this.attachmentService.documents.length > 0) {
      let doc = this.attachmentService.documents.find(i => i.id == id);
      if (doc != null) {
        result = {
            fileName: doc.fileName,
            createdAt: doc.createdAt,
            documentId: doc.id,
            fileKey: doc.fileKey
        } as Attachment;
      }
    }
    //Registration
    else if (!this.isEntity && this.attachmentService.attachments.length > 0) {
      result = this.attachmentService.attachments.find(i => i.documentType == DocumentType.INCORPORATION_CERTIFICATE_DOCUMENT && i.documentId == id) || null;
    }

    return result;
  }

  private getWorldCheckDocuments(ids: number[]): Attachment[] {
    return (ids || [])
      .map(id => this.getWorldCheckDocument(id))
      .filter((att): att is Attachment => att != null);
  }

  private getWorldCheckDocument(id: number): Attachment | null {
    let result: Attachment | null = null;

    // Registered Entity
    if (this.isEntity && this.attachmentService.documents.length > 0) {
      let doc = this.attachmentService.documents.find(i => i.id == id);
      if (doc != null) {
        result = {
            fileName: doc.fileName,
            createdAt: doc.createdAt,
            documentId: doc.id,
            fileKey: doc.fileKey
        } as Attachment;
      }
    }
    //Registration
    else if (!this.isEntity && this.attachmentService.attachments.length > 0) {
      result = this.attachmentService.attachments.find(i => i.documentType == DocumentType.WORLD_CHECK && i.documentId == id) || null;
    }

    return result;
  }

  setQualifiedRecipientLines() {
    this.qualifiedRecipientLines = [];
    if (this.bodycorp.qualifiedRecipient?.includes("personHoldingEntitlement")) {
      this.qualifiedRecipientLines.push("a Person holding an entitlement specified in, or pursuant to, the By-laws to a fixed share of the property and income of the Foundation when the Foundation distributes it;");
    }
    if (this.bodycorp.qualifiedRecipient?.includes("personHoldingDepositoryReceipt")) {
      this.qualifiedRecipientLines.push("a Person holding a Depository Receipt;");
    }
    if (this.bodycorp.qualifiedRecipient?.includes("prospectiveRecipient")) {
      this.qualifiedRecipientLines.push("a Person who is a prospective recipient of a fixed, or discretionary, share of the property of the Foundation upon the happening of a future event specified in the By-laws;");
    }
    if (this.bodycorp.qualifiedRecipient?.includes("nominatedPursuant")) {
      this.qualifiedRecipientLines.push("a Person who is nominated pursuant to the By-laws to be a recipient of a fixed, or discretionary, share of the property and income of the Foundation at a time following the establishment of the Foundation;");
    }
    if (this.bodycorp.qualifiedRecipient?.includes("charity")) {
      this.qualifiedRecipientLines.push("a charity;");
    }
    if (this.bodycorp.qualifiedRecipient?.includes("defaultRecipient")) {
      this.qualifiedRecipientLines.push("a default recipient;");
    }
  }

  public readonly nzModalData: any = inject(NZ_MODAL_DATA);

  mapRoleToText(role: string): string {
    return ROLES_MESSAGE_MAP.get(role) || role;
  }

  getFilteredJobTitlesBodyCorporated(bodyCorp: StateBodyCorporate): Roles[] {
    return bodyCorp.jobTitles.map(role => role.titleCode as Roles).filter(role => !this.excludedRoles.includes(role));
  }

  isQualifiedRecipient(): boolean {
    return this.qualifiedRecipientLines.length > 0;
  }
  
  previewFile(att: Attachment) {
    if (this.isEntity) {
      this.regEntityDocumentPreviewManager.openPreview({fileKey: att.fileKey, fileName: att.fileName});
    }
    else {
      this.attachmentPreviewManager.openPreview({ fileId: att.documentId, fileName: att.fileName, applicationId: att.backOfficeApplicationId});
    }
  }
  
  previewKYBFile(att: Attachment) {
    this.documentPreviewManager.openPreview({ fileId: att.documentId, fileName: att.fileName});
  }
}
